import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";
import { FollowerCounts } from "../../models/comedian-weekly-digest";

const PAGE_SIZE = 1000;

/**
 * Fetches total follower counts and new-this-week counts for all comedians
 * from the pre-aggregated comedian_followers_summary table.
 *
 * Uses total_followers for the cumulative count and new_followers_7d for the
 * rolling 7-day new follower count. The 7d window is relative to the table's
 * last_updated timestamp rather than aligned to a specific digest week
 * boundary, but close enough for digest purposes.
 */
export async function fetchFollowerCounts(
  supabase: SupabaseClient<Database>,
  _weekStart: string
): Promise<Map<string, FollowerCounts>> {
  const result = new Map<string, FollowerCounts>();
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from("comedian_followers_summary")
      .select("comedian_id, total_followers, new_followers_7d")
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-weekly-digest] Failed to fetch follower counts from comedian_followers_summary",
      });
      throw error;
    }

    if (!data || data.length === 0) break;

    for (const row of data) {
      result.set(row.comedian_id, {
        total: row.total_followers ?? 0,
        newThisWeek: row.new_followers_7d ?? 0,
      });
    }

    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  return result;
}
